import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useApolloClient, useQuery, useSubscription } from "@apollo/client/react";
import isEqual from "lodash/isEqual";
import { useNavigate, useLocation } from "react-router";

import { mergeAssetPatch } from "../utils/asset";
import {
  ADD_ASSET_IMAGE_MUTATION,
  ASSET_MUTATED_SUBSCRIPTION,
  ASSET_DELETED_SUBSCRIPTION,
  ASSETS_BULK_REMOVE_MUTATION,
  ASSETS_COLLECTION_IDS_QUERY,
  ASSETS_COLLECTION_TABLE_QUERY,
  BULK_CREATE_ASSET_IMAGES_MUTATION,
  CREATE_ASSET_MUTATION,
  DELETE_IMAGE_MUTATION,
  INVENTORY_ASSET_STOCKED,
  REMOVE_ASSET_MUTATION,
  UPDATE_ASSET_MUTATION,
  UPDATE_IMAGE_MUTATION,
} from "../constants/graphql";
import useMutation from "../hooks/useMutation";
import useNotification from "../hooks/notification";
import useQueryParams from "../hooks/useQuery";
import {
  addRecord,
  removeCollection,
  replaceRecord,
  updateCollectionQuery,
} from "../components/TablePro/utils";
import { ASSETS_COLLECTION_INVENTORY_ITEMS_QUERY } from "../constants/graphql/queries";
import { ProjectContext } from "./project";

const NOTIFICATIONS = {
  assetCreated: "New Asset was added",
  assetUpdated: "Asset was updated",
  assetRemoved: "Asset was removed",
  imagesUploaded: "Images have been uploaded",
};

const AssetsCollectionContext = React.createContext(null);

export const AssetsCollectionProvider = ({
  children,
  variables = {},
  query = ASSETS_COLLECTION_TABLE_QUERY,
  includeInventoryItems = false,
  type,
  skipQuery = false,
}) => {
  const mounted = useRef(false);
  const queryParams = useQueryParams();
  const navigate = useNavigate();
  const location = useLocation();
  const client = useApolloClient();
  const { notifyError, notifySuccess } = useNotification();
  const [loading, setLoading] = useState(!skipQuery);
  const [loadingMore, setLoadingMore] = useState(false);
  const [loadingMutation, setLoadingMutation] = useState(false);
  const [collection, setCollection] = useState([]);
  const [metadata, setMetadata] = useState({});
  const [inventoryItemsData, setInventoryItemsData] = useState({});

  const [createAsset] = useMutation(CREATE_ASSET_MUTATION);
  const [updateAsset] = useMutation(UPDATE_ASSET_MUTATION);
  const [removeAsset] = useMutation(REMOVE_ASSET_MUTATION);
  const [removeImage] = useMutation(DELETE_IMAGE_MUTATION);
  const [createImage] = useMutation(ADD_ASSET_IMAGE_MUTATION);
  const [updateImage] = useMutation(UPDATE_IMAGE_MUTATION);
  const [bulkRemoveAssets] = useMutation(ASSETS_BULK_REMOVE_MUTATION);
  const [bulkCreateImages] = useMutation(BULK_CREATE_ASSET_IMAGES_MUTATION);

  const [limit, setLimit] = useState(variables.limit || 40);
  const search = queryParams.get("search") || variables.search || "";

  const handleSearchChange = newSearch => {
    const params = new URLSearchParams(location.search);

    if (newSearch) {
      params.set("search", newSearch);
    } else {
      params.delete("search");
    }

    navigate({
      search: params.toString(),
    }, { replace: true });
  };

  // Handle both single type (string) and multiple types (array) for backwards compatibility
  // Priority: explicit type prop > variables.type/types
  const typeValue = type !== undefined ? type : (variables.types || variables.type);
  const typeFilter = Array.isArray(typeValue)
    ? { types: typeValue, type: undefined }
    : typeValue
    ? { type: typeValue, types: undefined }
    : {};

  const collectionFilter = {
    ...variables,
    search,
    limit,
    page: variables.page || 1,
    ...typeFilter,
  };

  const collectionFilterKey = JSON.stringify(collectionFilter);
  const identityKey = `${collectionFilter.clientId ?? ""}:${collectionFilter.projectId ?? ""}`;
  const [scopedIdentityKey, setScopedIdentityKey] = useState(identityKey);
  const refetchGenerationRef = useRef(0);

  // Client/project is a different inventory scope. Drop the previous list immediately
  // so Boston assets cannot remain visible under Austin while the new query is in flight
  // or a stale refetch completes later. Inventory chips (warehouse, in/out of stock)
  // keep the current list until `handeRefetch` finishes — that path is what #6093 /
  // BUGS-165 relies on for count updates.
  if (scopedIdentityKey !== identityKey) {
    setScopedIdentityKey(identityKey);
    setCollection([]);
    setMetadata({});
    setInventoryItemsData({});
    setLoading(!skipQuery);
    refetchGenerationRef.current += 1;
  }

  const { data, refetch: refetchInventoryItems } = useQuery(
    ASSETS_COLLECTION_INVENTORY_ITEMS_QUERY,
    {
      fetchPolicy: "network-only",
      nextFetchPolicy: "cache-first",
      skip: !includeInventoryItems,
      variables: collectionFilter,
    }
  );

  const assetsWithInventoryItems = useMemo(() => {
    return includeInventoryItems
      ? collection.map(item => ({
          ...item,
          inventoryItems: inventoryItemsData[item.id] || [],
        }))
      : collection;
  }, [includeInventoryItems, collection, inventoryItemsData]);

  useEffect(() => {
    if (data?.assetsCollection?.collection) {
      setInventoryItemsData(prevData => ({
        ...prevData,
        ...data.assetsCollection.collection.reduce((acc, item) => {
          acc[item.id] = item.inventoryItems;
          return acc;
        }, {}),
      }));
    }
  }, [data]);

  useEffect(() => {
    if (!mounted.current) {
      mounted.current = true;
    } else {
      handeRefetch();
    }
  }, [collectionFilterKey]);

  // Apollo re-runs a `network-only` watched query every time its variables
  // change, but after the first hydration (below) its emissions are ignored:
  // every filter change already refetches through `handeRefetch`. Pin the
  // watched query to the variables it hydrated with so a filter change issues
  // one request instead of two. The discarded second request was aborted on
  // the next change/unmount and showed up as cancelled `GetAssetsTableCollection`
  // requests (H27) in production. `fetchMore` passes its full variables
  // explicitly, so pagination is unaffected.
  const hydratedVariablesRef = useRef(null);
  const {
    fetchMore,
    data: queryData,
    variables: queryVariables,
  } = useQuery(query, {
    fetchPolicy: "network-only",
    nextFetchPolicy: "cache-first",
    variables: hydratedVariablesRef.current || collectionFilter,
    skip: skipQuery,
  });

  // Hydrate the local collection from the query ONCE, on initial load.
  //
  // After that we deliberately ignore further `queryData` emissions. Apollo
  // re-emits this watched query every time a subscription writes a normalized
  // asset into the cache, and blindly resetting `collection` to the cached page
  // wipes assets that were just added live via `addRecord` (the disappearing
  // asset bug from #6087). Subsequent state is owned by the live subscription
  // reducers, `handeRefetch` (filter changes), and `onLoadMore` (pagination).
  // Do not reset this guard on client/filter changes — that would reintroduce #6087.
  const hasHydratedFromQueryRef = useRef(false);

  useEffect(() => {
    if (queryData && !skipQuery && !hasHydratedFromQueryRef.current) {
      hasHydratedFromQueryRef.current = true;
      hydratedVariablesRef.current = queryVariables || collectionFilter;
      onFetch(queryData);
    }
  }, [queryData]);

  const onFetch = ({ assetsCollection = {} } = {}, generation = refetchGenerationRef.current) => {
    if (generation !== refetchGenerationRef.current) return;

    setCollection(assetsCollection?.collection || []);
    setMetadata(assetsCollection?.metadata || {});
    setLoading(false);
  };

  const handeRefetch = () => {
    const generation = ++refetchGenerationRef.current;
    setLoading(true);
    const currentPage = metadata.currentPage || 1;
    const itemsToFetch = currentPage * limit;
    const variables = {
      ...collectionFilter,
      page: 1,
      limit: itemsToFetch,
    };
    client
      .query({ query, variables, fetchPolicy: "network-only" })
      .then(({ data }) => {
        if (generation !== refetchGenerationRef.current) return;
        setCollection(data.assetsCollection?.collection || []);
        setMetadata(data.assetsCollection?.metadata || {});
      })
      .finally(() => {
        if (generation === refetchGenerationRef.current) {
          setLoading(false);
        }
      });
  };

  // Select-all needs every matching id, not just the loaded page. The table query
  // no longer carries `totalIds` (140k+ ids per page view on large tenants); fetch
  // them on demand with the current filters and cache them on metadata so the
  // add/remove reducers and a second select-all can reuse them.
  const collectionFilterRef = useRef(collectionFilter);
  collectionFilterRef.current = collectionFilter;

  const fetchTotalIds = useCallback(() => {
    const cached = metadataRef.current.totalIds;
    if (Array.isArray(cached) && cached.length > 0) return Promise.resolve(cached);

    return client
      .query({
        query: ASSETS_COLLECTION_IDS_QUERY,
        variables: { ...collectionFilterRef.current, page: 1, limit: 1 },
        fetchPolicy: "network-only",
      })
      .then(({ data }) => {
        const ids = data?.assetsCollection?.metadata?.totalIds || [];
        setMetadata((prev) => ({ ...prev, totalIds: ids }));
        return ids;
      });
  }, [client]);

  const onLoadMore = () => {
    if (loading || loadingMore) return;
    const generation = refetchGenerationRef.current;
    setLoadingMore(true);
    fetchMore({
      variables: { ...collectionFilter, page: metadata.currentPage + 1 },
      updateQuery: updateCollectionQuery("assetsCollection", {
        collection,
        metadata,
        onSuccess: (data) => {
          onFetch(data, generation);
          setLoadingMore(false);
        },
        onFailure: () => {
          setLoadingMore(false);
        },
      }),
    });
    if (includeInventoryItems) {
      refetchInventoryItems({ ...collectionFilter, page: metadata.currentPage + 1 });
    }
  };

  // Resolve clientId for client-scoped subscriptions.
  // On `/projects/:projectId/...` routes we get the project from context (no duplicate query needed)
  // Use React.useContext directly - returns null if not in ProjectProvider, which is fine
  const projectFromContext = React.useContext(ProjectContext);

  const realClientId = useMemo(() => {
    if (variables.clientId && variables.clientId !== "null") {
      return variables.clientId;
    }

    // Use project from context first (eliminates duplicate query), fallback to collection
    return projectFromContext?.client?.id || collection?.[0]?.client?.id;
  }, [variables.clientId, projectFromContext, collection]);

  // Use refs to avoid stale closures in subscription callbacks.
  // This ensures we always have access to the latest values when subscription events fire.
  const collectionRef = useRef(collection);
  const metadataRef = useRef(metadata);
  metadataRef.current = metadata;
  const variablesRef = useRef(variables);
  const typeFilterRef = useRef(typeValue);
  const projectRef = useRef(projectFromContext);
  projectRef.current = projectFromContext;

  useEffect(() => {
    collectionRef.current = collection;
  }, [collection]);

  useEffect(() => {
    variablesRef.current = variables;
  }, [variables]);

  useEffect(() => {
    typeFilterRef.current = typeValue;
  }, [typeValue]);

  // Helper function to check if an incoming asset matches the current view filters.
  // This prevents assets from other projects/clients from being added to the collection.
  const assetMatchesCurrentFilters = useCallback((asset) => {
    if (!asset) return false;

    const currentVariables = variablesRef.current;
    const currentTypeFilter = typeFilterRef.current;

    // Check project filter - this is the critical filter for the bug being fixed
    if (currentVariables.projectId) {
      const assetProjectId = asset.projectId || asset.project?.id;
      // Convert both to strings for comparison since IDs can be strings or numbers
      if (String(assetProjectId) !== String(currentVariables.projectId)) {
        return false;
      }
    }

    // Check client filter
    if (currentVariables.clientId && currentVariables.clientId !== "null") {
      const assetClientId = asset.clientId || asset.client?.id;
      if (assetClientId && String(assetClientId) !== String(currentVariables.clientId)) {
        return false;
      }
    }

    // Check type filter (e.g., "Asset", "Kit", "GroupedAsset")
    if (currentTypeFilter) {
      const allowedTypes = Array.isArray(currentTypeFilter) ? currentTypeFilter : [currentTypeFilter];
      if (asset.type && !allowedTypes.includes(asset.type)) {
        return false;
      }
    }

    // Check "In Warehouse" filter. Mirrors NonDepletedAssetsQuery: a regular
    // asset with no rack locations has left the warehouse, unless its project
    // captures inventory onsite without rack locations.
    if (currentVariables.hideDepletedAssets && asset.type === "Asset") {
      const project = projectRef.current;
      const assetProjectId = asset.projectId || asset.project?.id;
      const isKnownWarehouseProject =
        project?.id &&
        String(project.id) === String(assetProjectId) &&
        !project.settings?.onsite_inventory_capture;
      const rackLocations = Array.isArray(asset.rackLocations) ? asset.rackLocations : [];

      if (isKnownWarehouseProject && rackLocations.length === 0) {
        return false;
      }
    }

    return true;
  }, []);

  useSubscription(INVENTORY_ASSET_STOCKED, {
    onData: ({ data: { data: { inventoryAssetStocked } = {} } = {} }) => {
      if (!inventoryAssetStocked?.id) return;

      const currentCollection = collectionRef.current;
      const existingAsset = currentCollection?.find(asset => asset.id === inventoryAssetStocked.id);
      if (!existingAsset) return;

      const { merged } = mergeAssetPatch(
        existingAsset,
        inventoryAssetStocked,
        inventoryAssetStocked.changedFields
      );

      if (!isEqual(existingAsset, merged)) {
        replaceRecord(setCollection, {
          id: inventoryAssetStocked.id,
          values: merged,
        });
      }
    },
  });

  useSubscription(ASSET_MUTATED_SUBSCRIPTION, {
    variables: { clientId: realClientId },
    skip: !realClientId || realClientId === "null",
    onData: ({ data: { data: { assetMutated } = {} } = {} }) => {
      if (!assetMutated) return;

      const currentCollection = collectionRef.current;
      const existingAsset = currentCollection?.find(asset => asset.id === assetMutated.id);

      if (existingAsset) {
        if (!assetMatchesCurrentFilters(assetMutated)) {
          removeCollection({ setCollection, setMetadata, ids: [assetMutated.id] });
          return;
        }

        const { merged } = mergeAssetPatch(
          existingAsset,
          assetMutated,
          assetMutated.changedFields
        );

        if (!isEqual(existingAsset, merged)) {
          replaceRecord(setCollection, {
            id: assetMutated.id,
            values: merged,
          });
        }
      } else {
        if (!assetMatchesCurrentFilters(assetMutated)) return;
        addRecord({ setCollection, setMetadata, record: assetMutated });
      }
    },
  });

  // Subscribe to asset deletions
  useSubscription(ASSET_DELETED_SUBSCRIPTION, {
    variables: { clientId: realClientId },
    skip: !realClientId || realClientId === "null",
    onData: ({ data: { data: { assetDeleted } = {} } = {} }) => {
      if (!assetDeleted) return;

      // Use ref to get current collection to avoid stale closure
      const currentCollection = collectionRef.current;
      const existingAsset = currentCollection?.find(asset => asset.id === assetDeleted.id);

      if (existingAsset) {
        removeCollection({ setCollection, setMetadata, ids: [assetDeleted.id] });
      }
    },
  });

  const handleUpdateAsset = ({ variables, onSuccess, onFailure, onConflict }) => {
    setLoadingMutation(true);

    return updateAsset({
      variables,
      onSuccess: (response) => {
        if (response.conflict) {
          const { asset } = response;
          if (asset) {
            replaceRecord(setCollection, {
              id: asset.id,
              values: asset,
            });
          }

          setLoadingMutation(false);
          notifyError("Some fields were modified by another user and were not overwritten.");
          if (onConflict) {
            onConflict(response.conflict, { asset });
          }
          if (onSuccess && asset) {
            onSuccess({ asset, conflict: response.conflict });
          }
          return;
        }

        const { asset } = response;
        replaceRecord(setCollection, {
          id: asset.id,
          values: asset,
        });

        setLoadingMutation(false);
        notifySuccess(NOTIFICATIONS.assetUpdated);
        if (onSuccess) onSuccess({ asset });
      },
      onFailure: errors => {
        notifyError(errors);
        setLoadingMutation(false);
        if (onFailure) onFailure(errors);
      },
    });
  };

  const handleCreateAsset = ({ variables, onSuccess, onFailure }) => {
    setLoadingMutation(true);

    return createAsset({
      variables: variables,
      onSuccess: ({ asset }) => {
        addRecord({ setCollection, setMetadata, record: asset });
        setLoadingMutation(false);
        notifySuccess(NOTIFICATIONS.assetCreated);
        if (onSuccess) onSuccess({ asset });
      },
      onFailure: errors => {
        setLoadingMutation(false);
        if (onFailure) onFailure(errors);
      },
    });
  };

  const handleRemoveAsset = ({ variables, onSuccess, onFailure }) => {
    setLoadingMutation(true);

    return removeAsset({
      variables,
      onSuccess: ({ asset }) => {
        removeCollection({ setCollection, setMetadata, ids: [asset.id] });
        setLoadingMutation(false);
        notifySuccess(NOTIFICATIONS.assetRemoved);
        onSuccess && onSuccess({ asset });
      },
      onFailure: errors => {
        setLoadingMutation(false);
        onFailure && onFailure(errors);
      },
    });
  };

  const handleRemoveImage = ({ variables, onSuccess, onFailure }) => {
    setLoadingMutation(true);

    removeImage({
      variables,
      onSuccess: ({ asset }) => {
        replaceRecord(setCollection, {
          id: asset.id,
          values: { images: asset.images, photo: asset.photo },
        });
        setLoadingMutation(false);
        onSuccess && onSuccess({ asset });
      },
      onFailure: errors => {
        notifyError(errors);
        setLoadingMutation(false);
        onFailure && onFailure(errors);
      },
    });
  };

  const handleCreateImage = ({ variables, onSuccess, onFailure }) => {
    setLoadingMutation(true);
    const defaultImage = collection.find(v => v.id === variables.assetId).images.length === 0;

    return createImage({
      variables: { ...variables, default: defaultImage },
      onSuccess: ({ asset }) => {
        replaceRecord(setCollection, {
          id: asset.id,
          values: { images: asset.images, photo: asset.photo },
        });

        setLoadingMutation(false);
        onSuccess && onSuccess({ asset });
      },
      onFailure: errors => {
        notifyError(errors);
        setLoadingMutation(false);
        onFailure && onFailure(errors);
      },
    });
  };

  const handleBulkCreateImages = ({
    variables,
    onSuccess,
    onFailure,
    disableNotification = false,
  }) => {
    setLoadingMutation(true);

    return bulkCreateImages({
      variables,
      onSuccess: ({ asset, images }) => {
        replaceRecord(setCollection, {
          id: asset.id,
          values: { images: asset.images, photo: asset.photo },
        });

        setLoadingMutation(false);
        onSuccess && onSuccess({ asset, images });
        if (!disableNotification) {
          notifySuccess(NOTIFICATIONS.imagesUploaded);
        }
      },
      onFailure: errors => {
        notifyError(errors);
        setLoadingMutation(false);
        onFailure && onFailure(errors);
      },
    });
  };

  const handleUpdateImage = ({ variables, onSuccess, onFailure }) => {
    setLoadingMutation(true);

    updateImage({
      variables,
      onSuccess: ({ asset }) => {
        replaceRecord(setCollection, {
          id: asset.id,
          values: { images: asset.images, photo: asset.photo },
        });
        setLoadingMutation(false);
        onSuccess && onSuccess({ asset });
      },
      onFailure: errors => {
        notifyError(errors);
        setLoadingMutation(false);
        onFailure && onFailure(errors);
      },
    });
  };

  const handleBulkRemove = ({ variables, onSuccess, onFailure }) => {
    const { ids } = variables;
    setLoadingMutation(true);

    bulkRemoveAssets({
      variables,
      onSuccess: () => {
        removeCollection({ setCollection, setMetadata, ids });
        setLoadingMutation(false);
        onSuccess && onSuccess();
      },
      onFailure: errors => {
        setLoadingMutation(false);
        onFailure && onFailure(errors);
      },
    });
  };

  const collectionIds = useMemo(() => collection?.map(entity => entity.id) || [], [collection]);

  const removeAssetsFromCollection = ids => {
    removeCollection({ setCollection, setMetadata, ids });
  };

  const value = {
    // Collection
    loading,
    setLoading,
    loadingMore,
    collection: assetsWithInventoryItems,
    setCollection,
    collectionIds,
    handeRefetch,
    onLoadMore,
    search,
    setSearch: handleSearchChange,
    limit,
    setLimit,

    // Metadata
    setMetadata,
    metadata,
    ...metadata,
    totalIds: metadata.totalIds || [],
    fetchTotalIds,

    // Mutation
    loadingMutation,
    setLoadingMutation,
    removeAssetsFromCollection,
    removeAsset: handleRemoveAsset,
    createAsset: handleCreateAsset,
    updateAsset: handleUpdateAsset,
    removeImage: handleRemoveImage,
    createImage: handleCreateImage,
    updateImage: handleUpdateImage,
    bulkRemoveAssets: handleBulkRemove,
    bulkCreateImages: handleBulkCreateImages,
  };

  return (
    <AssetsCollectionContext.Provider value={value}>{children}</AssetsCollectionContext.Provider>
  );
};

export const useAssetsCollection = () => {
  const context = React.useContext(AssetsCollectionContext);
  if (!context) {
    throw new Error("useAssetsCollection must be used within a AssetsCollectionProvider");
  }
  return context;
};

// Safe version that returns null instead of throwing when used outside AssetsCollectionProvider
export const useAssetsCollectionSafe = () => {
  return React.useContext(AssetsCollectionContext);
};
